import { NavLink } from 'react-router-dom'
import { LayoutDashboard, Users, CreditCard, Blocks, BarChart3, LogOut, ShieldCheck, ClipboardList, Paintbrush, ArrowUpCircle, Settings } from 'lucide-react'
import { useQuery } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import { useAuthStore } from '@/stores/auth-store'
import { adminApi } from '@/lib/api'
import { cn } from '@/lib/utils'

const NAV_GROUPS = [
  {
    items: [
      { to: '/', label: 'Dashboard', icon: LayoutDashboard, end: true },
    ],
  },
  {
    label: 'Manajemen',
    items: [
      { to: '/tenants', label: 'Tenant', icon: Users, end: false },
      { to: '/registrations', label: 'Registrasi', icon: ClipboardList, end: false },
      { to: '/upgrade-requests', label: 'Upgrade Plan', icon: ArrowUpCircle, end: false },
    ],
  },
  {
    label: 'Produk',
    items: [
      { to: '/plans', label: 'Plan & Fitur', icon: CreditCard, end: false },
      { to: '/components', label: 'Komponen', icon: Blocks, end: false },
      { to: '/theme-presets', label: 'Tema Preset', icon: Paintbrush, end: false },
    ],
  },
  {
    label: 'Insight',
    items: [
      { to: '/analytics', label: 'Analitik', icon: BarChart3, end: false },
    ],
  },
  {
    label: 'Platform',
    items: [
      { to: '/platform-settings', label: 'Pengaturan', icon: Settings, end: false },
    ],
  },
]

export function Sidebar() {
  const clear = useAuthStore((s) => s.clear)
  const user = useAuthStore((s) => s.user)

  const { data: registrations = [] } = useQuery({
    queryKey: ['admin', 'registrations'],
    queryFn: adminApi.getRegistrations,
    staleTime: 1000 * 30,
  })
  const pendingRegistrations = registrations.filter((r) => r.status === 'pending').length

  const { data: overview } = useQuery({
    queryKey: ['admin', 'overview'],
    queryFn: adminApi.getOverview,
    staleTime: 1000 * 60,
  })
  const pendingUpgrades = overview?.pending_upgrade_requests ?? 0

  async function handleSignOut() {
    await supabase.auth.signOut()
    clear()
  }

  return (
    <aside className="flex h-full w-60 flex-col border-r border-sidebar-border bg-sidebar">
      {/* Logo */}
      <div className="flex h-14 items-center gap-2 border-b border-sidebar-border px-4">
        <div className="flex h-7 w-7 items-center justify-center rounded-md bg-sidebar-primary text-sidebar-primary-foreground">
          <ShieldCheck className="h-4 w-4" />
        </div>
        <span className="font-semibold text-sidebar-foreground">OneIdol Admin</span>
      </div>

      {/* Navigation */}
      <nav className="flex-1 overflow-y-auto py-4 px-2 space-y-4">
        {NAV_GROUPS.map((group, gi) => (
          <div key={gi}>
            {group.label && (
              <p className="mb-1 px-3 text-xs font-semibold uppercase tracking-wider text-sidebar-foreground/40">
                {group.label}
              </p>
            )}
            <div className="space-y-0.5">
              {group.items.map((item) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  end={item.end}
                  className={({ isActive }) =>
                    cn(
                      'flex items-center gap-3 rounded-md px-3 py-2 text-sm transition-colors',
                      isActive
                        ? 'bg-sidebar-accent text-sidebar-accent-foreground font-medium'
                        : 'text-sidebar-foreground hover:bg-sidebar-accent hover:text-sidebar-accent-foreground'
                    )
                  }
                >
                  <item.icon className="h-4 w-4 shrink-0" />
                  <span className="flex-1">{item.label}</span>
                  {item.to === '/registrations' && pendingRegistrations > 0 && (
                    <span className="flex h-5 min-w-5 items-center justify-center rounded-full bg-yellow-500 px-1 text-xs font-bold text-white">
                      {pendingRegistrations}
                    </span>
                  )}
                  {item.to === '/upgrade-requests' && pendingUpgrades > 0 && (
                    <span className="flex h-5 min-w-5 items-center justify-center rounded-full bg-yellow-500 px-1 text-xs font-bold text-white">
                      {pendingUpgrades}
                    </span>
                  )}
                </NavLink>
              ))}
            </div>
          </div>
        ))}
      </nav>

      {/* Footer */}
      <div className="border-t border-sidebar-border p-3">
        <div className="mb-2 px-3 py-1">
          <p className="text-xs text-muted-foreground truncate">{user?.email}</p>
        </div>
        <button
          onClick={handleSignOut}
          className="flex w-full items-center gap-3 rounded-md px-3 py-2 text-sm text-sidebar-foreground hover:bg-sidebar-accent hover:text-sidebar-accent-foreground transition-colors"
        >
          <LogOut className="h-4 w-4 shrink-0" />
          Keluar
        </button>
      </div>
    </aside>
  )
}
